<template>
  <div class="signature-pad-wrapper">
    <div class="signature-pad">
      <div class="signature-pad--body" :style="{ height: height + 'px' }">
        <canvas v-show="!signImageUrl" ref="canvasRef" class="canvasId" />
        <img v-if="signImageUrl" :src="signImageUrl" class="signature-image">
      </div>
      <p class="desc-text">请在上面区域完成签名，然后点击"确认"按钮</p>
      <div v-if="!signImageUrl && !disabled" class="signature-actions">
        <el-button plain size="mini" type="danger" @click="clear">清除</el-button>
        <el-button plain size="mini" type="warning" @click="undo">回撤</el-button>
        <el-button size="mini" type="success" @click="savePng">确认</el-button>
      </div>
      <div v-if="signImageUrl && !disabled" class="signature-actions">
        <el-button size="mini" type="primary" @click="reSign">重签</el-button>
      </div>
    </div>
  </div>
</template>

<script>
import SignaturePad from 'signature_pad'
import { upload } from '@/api/sys/file'


/**
 * 电子签名组件
 * @description 基于 signature_pad 实现的电子签名组件，支持绘制签名并上传保存
 */
export default {
  name: 'SignPad',
  components: {},
  props: {
    // 签名图片URL（双向绑定）
    value: {
      type: String,
      default: ''
    },
    // 画笔颜色
    color: {
      type: String,
      default: '#000000'
    },
    // 文件上传地址
    action: {
      type: String,
      default: process.env.VUE_APP_BASE_API + '/api/sys/file/upload'
    },
    // 签名区域高度
    height: {
      type: Number,
      default: 200
    },
    // 是否禁用
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      signaturePad: null,
      canvas: null,
      signImageUrl: '',
      config: {
        minWidth: 1,
        maxWidth: 3,
        penColor: this.color
      }
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(val) {
        this.signImageUrl = val || ''
      }
    },
    color(val) {
      if (this.signaturePad) {
        this.signaturePad.penColor = val
      }
    }
  },
  mounted() {
    this.$nextTick(() => {
      // 延迟初始化，确保父容器已经有宽度
      setTimeout(() => {
        this.init()
        this.resizeCanvas()
      }, 100)
      window.addEventListener('resize', this.handleResize)
    })
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    /**
     * 处理窗口大小变化
     */
    handleResize() {
      this.resizeCanvas()
    },
    /**
     * 重新调整画布大小
     */
    resizeCanvas() {
      const canvas = this.canvas
      if (!canvas) {
        return
      }
      const wrapper = canvas.parentElement
      if (!wrapper) {
        return
      }
      // 获取父容器的实际宽度
      const width = wrapper.offsetWidth || 300
      const height = this.height || 200
      // 获取设备像素比
      const ratio = Math.max(window.devicePixelRatio || 1, 1)
      // 设置画布尺寸
      canvas.width = width * ratio
      canvas.height = height * ratio
      canvas.style.width = width + 'px'
      canvas.style.height = height + 'px'
      canvas.getContext('2d').scale(ratio, ratio)
      // 清除签名板数据
      if (this.signaturePad) {
        this.signaturePad.clear()
      }
    },
    /**
     * 初始化签名板
     */
    init() {
      this.canvas = this.$refs.canvasRef
      if (!this.canvas) {
        return
      }
      this.signaturePad = new SignaturePad(this.canvas, {
        ...this.config,
        penColor: this.color
      })
    },
    /**
     * 清除签名
     */
    clear() {
      if (this.signaturePad) {
        this.signaturePad.clear()
      }
    },
    /**
     * 回撤上一笔
     */
    undo() {
      if (!this.signaturePad) {
        return
      }
      const data = this.signaturePad.toData()
      if (data && data.length > 0) {
        data.pop()
        this.signaturePad.fromData(data)
      }
    },
    /**
     * 保存签名为PNG图片并上传
     */
    savePng() {
      if (!this.signaturePad) {
        return
      }
      if (this.signaturePad.isEmpty()) {
        this.$message.warning('请先完成签名')
        return
      }
      const data = this.signaturePad.toDataURL('image/png')
      const file = this.dataURLtoFile(data, `signature_${new Date().getTime()}.png`)
      const param = new FormData()
      param.append('file', file)
      upload(param).then(res => {
        this.signImageUrl = res.data[0].fileUrl
        this.$emit('input', res.data[0].fileUrl)
        this.$emit('change', res.data[0].fileUrl)
        this.$message.success('签名保存成功' + res.data[0].fileUrl)
      }).catch(() => {
        this.$message.error('签名保存失败，请重试')
      })
    },
    /**
     * 重新签名
     */
    reSign() {
      this.signImageUrl = ''
      this.$emit('input', '')
      this.$emit('change', '')
      this.$nextTick(() => {
        setTimeout(() => {
          if (this.signaturePad) {
            this.signaturePad.clear()
          }
          this.resizeCanvas()
        }, 100)
      })
    },
    /**
     * 将DataURL转换为File对象
     * @param {string} dataurl - base64编码的图片数据
     * @param {string} filename - 文件名
     * @returns {File} 文件对象
     */
    dataURLtoFile(dataurl, filename) {
      const arr = dataurl.split(',')
      const bstr = window.atob(arr[1])
      let n = bstr.length
      const u8arr = new Uint8Array(n)
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n)
      }
      return new File([u8arr], filename, {
        type: 'image/png'
      })
    }
  }
}
</script>

<style scoped>
.signature-pad-wrapper {
  width: 100%;
  box-sizing: border-box;
}

.signature-pad {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 600px;
  background-color: #fff;
  border-radius: 4px;
  padding: 16px;
  box-sizing: border-box;
}

.signature-pad--body {
  position: relative;
  width: 100%;
  border: 2px dashed #dcdfe6;
  border-radius: 4px;
  background-color: #fafafa;
  box-sizing: border-box;
}

.signature-pad--body canvas {
  display: block;
  width: 100%;
  height: 100%;
  border-radius: 4px;
  cursor: crosshair;
}

.signature-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: 4px;
}

.desc-text {
  text-align: center;
  color: #909399;
  font-size: 12px;
  margin: 10px 0;
}

.signature-actions {
  text-align: center;
}
</style>
